// External imports
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import { useForm, FormProvider } from "react-hook-form";
import { Accordion } from "react-bootstrap";
import Cookies from "js-cookie";

// Internal imports
import testIds from "constants/testIds";
import Alert from "components/molecules/Alert/Alert";
import Card from "components/molecules/Card/Card";
import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import CardNew from "components/molecules/Card/CardNew";
import errorToast from "helpers/errorToast";
import getLabel from "helpers/getLabel";
import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ReactComponent as EditPencil } from "assets/images/icons/EditPencil.svg";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import Button from "components/atoms/Button/Button";
import Hr from "components/atoms/Hr/Hr";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import usePollingAction from "hooks/usePollingAction";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import ReferenceUpdates from "./components/ReferenceUpdates/ReferenceUpdates";
import ReferencesDialog from "./components/ReferencesDialog/ReferencesDialog";
import VesselUpdates from "../VesselTracking/VesselUpdates";
import "./Tracking.scss";
import ContainerTracking from "./components/ContainerTracking/ContainerTracking";

/**
 * Remove slashes from a string
 * @param {*} text - The text to remove slashes from
 * @returns {string} - The text with slashes removed
 */
function removeSlashes(text) {
  if (text && text.includes("||")) return text.split("||").join("");
  return text;
}

const Tracking = function ({
  openReferencesDialog,
  isReferencesDialogOpen,
  bookingReference,
  isLoading,
  closeReferencesDialog,
  refetchTrackingUpdates,
  refetchShipmentDetails,
  id,
  shipmentDetails,
  trackingUpdates,
}) {
  // Hooks
  const intl = useIntl();
  const methods = useForm({ shouldFocusError: true, mode: "all" });
  const [isCustomBroker] = useCustomBrokerPermission();
  const { isPollingInProgress, startPolling } = usePollingAction();
  // State
  const [trackingReferenceOptions, setTrackingReferenceOptions] = useState([]);
  const [references, setReferences] = useState([]);
  const [hoveredReference, setHoveredReference] = useState(null);
  const [selectedReferenceId, setSelectedReferenceId] = useState(null);
  const [selectedReferenceIsBookingReference, setSelectedReferenceIsBookingReference] = useState(false);
  const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  const [vesselUpdates] = useState([]);
  const [isVesselUpdatesLoading] = useState(false);
  const [vesselError] = useState(null);
  const containerReferences = useMemo(() => references.filter((item) => item.type === "container_no"), [references]);

  // Data declarations
  const freightMode = shipmentDetails?.service?.freight_mode || "";
  const freightModeType = shipmentDetails?.service?.freight_mode_type || "";
  const { data: shipmentData } = useGetShipmentBasic(id, {
    enabled: !!id,
    refetchOnMount: false,
  });
  const {
    data: trackingReferencesData,
    isLoading: isReferencesLoading,
    error,
    refetch: getReferences,
  } = useGetTrackingReferences(id, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { payment_location_reference } = shipmentData?.organization || {};

  useEffect(() => {
    if (trackingReferencesData) {
      const { tracking_settings = {}, references = [] } = trackingReferencesData || {};
      const { tracking_labels = {}, booking_reference_enabled } = tracking_settings;

      // Map over tracking labels to get tracking reference options
      const trackingOptions = Object.entries(tracking_labels).map(([key, value]) => ({ label: value, value: key }));
      const otherTrackingIndex = trackingOptions.findIndex((option) => option.label === "Other tracking");

      if (otherTrackingIndex !== -1) {
        trackingOptions[otherTrackingIndex].label = getLabel(
          "Other tracking",
          "capitalize-first",
          payment_location_reference,
          true,
        );
      }

      if (booking_reference_enabled !== "off") {
        trackingOptions.push({
          label: getLabel("Booking reference", "capitalize", null, true),
          value: "bookingReference",
        });
      }

      setTrackingReferenceOptions(trackingOptions);

      // Process references and set the state
      const updatedReferences = references || [];

      for (let i = 0; i < updatedReferences.length; i += 1) {
        if (updatedReferences[i].type === "primary" && i !== 0) {
          const primaryReference = updatedReferences.pop();
          updatedReferences.unshift(primaryReference);
        }
      }
      setReferences(updatedReferences);
    }

    if (error) {
      errorToast(
        error,
        intl.formatMessage({
          id: "tracking.retrieveTracking.errorMessage",
          defaultMessage: "Could not retrieve tracking details. Please try again later.",
        }),
      );
    }
  }, [trackingReferencesData]);

  // Clear the selected reference id when the reference dialog is closed
  useEffect(() => {
    if (!isReferencesDialogOpen) {
      setSelectedReferenceId(null);
      setSelectedReferenceIsBookingReference(false);
    }
  }, [isReferencesDialogOpen]);

  const onReferenceEditClick = (reference) => {
    if (typeof reference === "string") setSelectedReferenceIsBookingReference(true);
    openReferencesDialog(true);
    setSelectedReferenceId(reference.id);
  };

  // WEB-7810 - TODO: Re-enable this later when data being returned by Gatehouse is fixed.
  // // If the references change, check if there is a vessel_imo present. If so, get the vessel updates.
  // useEffect(() => {
  //   // Get vessel number
  //   const vesselNumber = references.find((reference) => reference.type === "vessel_imo")?.value;

  //   // If no vessel number, return
  //   if (!vesselNumber) return;

  //   // Remove "vessel-" from the vessel number
  //   const formattedVesselNumber = vesselNumber.replace("vessel-", "");

  //   // Get vessel updates
  //   getVesselUpdates(id, formattedVesselNumber)
  //     .then((res) => setVesselUpdates(res.data))
  //     .catch((err) => setVesselError(err.response.data.error))
  //     .finally(() => setIsVesselUpdatesLoading(false));
  // }, [references]);

  const renderReference = (reference) => {
    if (reference.tracking_number === "") return null;
    const allReferences = references.filter((item) => item.type !== "container_no");
    const hasContainerTracking = !!containerReferences.length;
    const referenceLabel = trackingReferenceOptions?.find((option) => option.value === reference.type)?.label;
    const referenceNumber = removeSlashes(reference.tracking_number);
    const referenceId = reference.id;
    const isLastReference = referenceId === allReferences[allReferences.length - 1].id;
    const hasBorder = !isLastReference || (isLastReference && bookingReference) || hasContainerTracking;
    const isAccordionOpen = activeAccordionKey === referenceId;

    const updates = trackingUpdates.filter(
      (update) =>
        (removeSlashes(update.group) === referenceNumber || update.group === referenceNumber) &&
        ["webhook", "schedule_change", "service_change", "auto_update", "flight"].includes(update.type),
    );

    const hasUpdates = updates.length > 0 || reference.type === "vessel_imo";

    // Default update values
    let updateTitle = <FormattedMessage id="general.noUpdates" defaultMessage="No updates" />;
    let updateDate = formatDate(new Date(), DateTimeFormat.shortDateTimeFormat);
    let updateMessage = "-";

    // If there are updates, replace the default values with the latest update values
    if (updates.length > 0) {
      const lastUpdate = updates[updates.length - 1];
      const location =
        lastUpdate.message.indexOf("Location:") !== -1
          ? lastUpdate.message.substring(lastUpdate.message.indexOf("Location:") + "Location:".length).trim()
          : lastUpdate.author_name;
      updateTitle = location === "Not available" ? lastUpdate.author_name : location;
      updateMessage = lastUpdate.message.split("Location:")[0].trim();
      updateDate = formatDate(lastUpdate.created, DateTimeFormat.shortDateTimeFormat);
    }

    if (reference.type === "vessel_imo" && vesselUpdates.length > 0) {
      // Get the latest port call (based on the event_datetime) with the event_classifier_code of "ACT"
      // This is the latest actual (not expected) port call
      const lastPortCall = vesselUpdates[0].port_calls
        .filter((portCall) => portCall.event_classifier_code === "ACT")
        .sort((a, b) => new Date(b.event_datetime) - new Date(a.event_datetime))[0];

      updateTitle = lastPortCall?.location_name.toLowerCase();
      updateDate = formatDate(new Date(lastPortCall?.event_datetime), DateTimeFormat.shortDateTimeFormat);
      updateMessage =
        lastPortCall?.transport_event_type_code === "DEPA"
          ? intl.formatMessage({ id: "tracking.departureFromPort", defaultMessage: "Departure from port" })
          : intl.formatMessage({ id: "tracking.arrivedFromPort", defaultMessage: "Arrived in port" });
    }

    return (
      <>
        <div className={`p-3  ${isAccordionOpen && hasUpdates ? "tracking-accordion-active" : ""}`} key={referenceId}>
          <Accordion.Toggle as="div" className={` ${hasUpdates ? "cursor-pointer" : ""} `} eventKey={referenceId}>
            <div className="d-flex justify-content-between">
              <div className="flex-grow-0" style={{ minWidth: 150 }}>
                {/* Reference label */}
                <p className="font-b4">{referenceLabel}</p>
                {/* Reference number */}
                <div
                  className="d-flex font-b2 mt-2"
                  onMouseEnter={() => setHoveredReference(referenceId)}
                  onMouseLeave={() => setHoveredReference(null)}
                >
                  <div
                  >
                    <CopyToClipboard text={referenceNumber} />
                  </div>
                  {hoveredReference === referenceId &&
                    Cookies.get("is_guest") !== "true" &&
                    !shipmentDetails?.viewing_as_guest_user && (
                      <EditPencil
                        style={{ marginTop: -2, marginLeft: 3, cursor: "pointer" }}
                        onClick={() => onReferenceEditClick(reference)}
                      />
                    )}
                </div>
              </div>
              {/* If the reference is a vessel_imo then display the loading skeleton whilst retrieving the vessel updates */}
              <div className="flex-grow-1">
                {isVesselUpdatesLoading && reference.type === "vessel_imo" ? (
                  // Display loading spinner while loading vessel updates
                  <div className="d-flex align-items-center h-100">
                    <span className="spinner-border spinner-border-sm  mr-2" />{" "}
                    <p className="font-b2">
                      <FormattedMessage id="tracking.retrievingUpdates" defaultMessage="Retrieving updates..." />
                    </p>
                  </div>
                ) : (
                  // Display content when loading is finished or for other reference types
                  <div>
                    <p className="font-b4 text-capitalize">
                      {updateTitle} {hasUpdates && <span className="text-secondary ml-2">{updateDate}</span>}
                    </p>
                    <p className="font-b2 mt-2">{updateMessage}</p>
                  </div>
                )}
              </div>
              {/* Accordion open/close icon */}
              {hasUpdates && (
                <SortIcon
                  className="my-auto"
                  fill="#000"
                  width={8}
                  height={7}
                  transform={`rotate(${isAccordionOpen ? 180 : 0})`}
                />
              )}
            </div>
          </Accordion.Toggle>
          {/* Accordion content */}
          {hasUpdates && (
            <Accordion.Collapse eventKey={referenceId}>
              <div className="mt-4">
                {/* We don't currently support container tracking */}
                {/* {reference.type === "container_no" && <TrackingUpdates shipmentId={id} containerNumber={referenceNumber} />} */}
                {reference.type !== "vessel_imo" && <ReferenceUpdates updates={updates} />}
                {reference.type === "vessel_imo" && (
                  <VesselUpdates
                    vesselUpdates={vesselUpdates}
                    vesselNumber={referenceNumber}
                    isLoading={isVesselUpdatesLoading}
                    error={vesselError}
                  />
                )}
              </div>
            </Accordion.Collapse>
          )}
        </div>

        {/* Border HR */}
        {hasBorder && <Hr />}
      </>
    );
  };

  const addReferenceOnClick = () => {
    openReferencesDialog();
  };

  return (
    <FormProvider {...methods}>
      <ReferencesDialog
        freightMode={freightMode}
        freightModeType={freightModeType}
        isOpen={isReferencesDialogOpen}
        close={closeReferencesDialog}
        prevReferences={references}
        bookingReference={bookingReference}
        typeOptions={trackingReferenceOptions}
        refreshTrackingData={(type) => {
          if (type && type === "container_no") {
            startPolling({
              fn: getReferences,
              delay: 3000,
              maxPollCount: 2,
              callFirstTimeImmediately: true,
            });
          }
          refetchShipmentDetails();
          refetchTrackingUpdates();
        }}
        shipmentId={id}
        referenceId={selectedReferenceId}
        isBookingReference={selectedReferenceIsBookingReference}
      />
      <CardNew isLoading={(isLoading || isReferencesLoading) && !isPollingInProgress} className="mr-9 ml-9">
        <Card.Body style={{ padding: 0 }}>
          {/* References */}
          <Accordion onSelect={(eventKey) => setActiveAccordionKey(eventKey)} activeKey={activeAccordionKey}>
            {references
              .filter((reference) => reference.type !== "container_no")
              .map((reference) => renderReference(reference))}
          </Accordion>
          <Accordion onSelect={(eventKey) => setActiveAccordionKey(eventKey)} activeKey={activeAccordionKey}>
            {containerReferences.map((reference, index) => (
              <>
                <ContainerTracking
                  key={reference.id}
                  reference={reference}
                  onReferenceEditClick={onReferenceEditClick}
                  activeAccordionKey={activeAccordionKey}
                  trackingReferenceOptions={trackingReferenceOptions}
                />
                {index !== containerReferences.length - 1 && <Hr />}
              </>
            ))}
          </Accordion>

          {/* Booking reference */}
          {bookingReference && (
            <div className="p-3">
              <p className="font-b4">{getLabel("Booking reference", "capitalize-first", payment_location_reference)}</p>
              <div
                className="d-flex"
                onMouseEnter={() => setHoveredReference("bookingRef")}
                onMouseLeave={() => setHoveredReference(null)}
              >
                <div
                  className="font-b2 mt-2"
                >
                  <CopyToClipboard text={bookingReference} />
                </div>
                {hoveredReference === "bookingRef" &&
                  Cookies.get("is_guest") !== "true" &&
                  !shipmentDetails?.viewing_as_guest_user && (
                    <EditPencil
                      style={{ marginTop: 5, marginLeft: 3, cursor: "pointer" }}
                      onClick={() => onReferenceEditClick(bookingReference)}
                    />
                  )}
              </div>
            </div>
          )}

          <div className="d-flex">
            {/* No references alert */}
            {references.length === 0 && Cookies.get("is_guest") !== "true" && !shipmentDetails?.viewing_as_guest_user && (
              <Alert className="mt-3 mb-3" variant="warning">
                <FormattedMessage
                  id="tracking.shipmentTrackingDetails"
                  defaultMessage="Please add shipment tracking details"
                />
              </Alert>
            )}

            {/* Add reference button */}
            {!isCustomBroker && Cookies.get("is_guest") !== "true" && !shipmentDetails?.viewing_as_guest_user && (
              <div className="mt-3 ml-auto">
                <Button
                  variant="outline-primary"
                  data-testid={testIds.SHIPMENT_DASHBOARD_TRACKING_REFERENCE_MODAL_BUTTON}
                  onClick={addReferenceOnClick}
                >
                  <FormattedMessage id="tracking.addTrackingNumber" defaultMessage="Add tracking number" />
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </CardNew>
    </FormProvider>
  );
};

Tracking.propTypes = {
  shipmentDetails: PropTypes.instanceOf(Object).isRequired,
  openReferencesDialog: PropTypes.func.isRequired,
  isReferencesDialogOpen: PropTypes.bool.isRequired,
  bookingReference: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  closeReferencesDialog: PropTypes.func.isRequired,
  refetchTrackingUpdates: PropTypes.func.isRequired,
  refetchShipmentDetails: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  trackingUpdates: PropTypes.instanceOf(Object).isRequired,
};

Tracking.defaultProps = {
  bookingReference: undefined,
};

export default Tracking;
